import React from 'react';
import {
	Alert,
	Avatar,
	Box,
	Button,
	Chip,
	Checkbox,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	IconButton,
	ImageList,
	ImageListItem,
	InputAdornment,
	Menu,
	MenuItem,
	Paper,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TablePagination,
	TableRow,
	TextField,
	Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import SyncIcon from '@mui/icons-material/Sync';
import StoreIcon from '@mui/icons-material/Store';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import DeleteIcon from '@mui/icons-material/Delete';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SaveIcon from '@mui/icons-material/Save';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import AddLinkIcon from '@mui/icons-material/AddLink';
import config from '../config';
import { useAuth } from '../auth/AuthContext';
import { useRealtime } from '../realtime/RealtimeContext';
import {
  ResizableColGroup,
  ResizableHeaderCell,
  TableEmptyRow,
  TableLoadingRow,
  useResizableColumns,
} from '../components/table/ResizableTable';
import ViewModeToggle from '../components/table/ViewModeToggle';
import DetailModalShell from '../components/detail/DetailModalShell';
import MockupsDataTabs from '../components/detail/MockupsDataTabs';
import { removeEntityById, upsertEntityById } from '../components/table/entityListPatch';
import { ProductDetailDialog } from './Products';

const debounceDelay = 300;
const defaultRowsPerPage = 25;
const getListingPreviewUrl = (item) => item?.image_url || item?.thumbnail_url || '';
const nilUUID = '00000000-0000-0000-0000-000000000000';
const hasLinkedPrintifyProduct = (id) => Boolean(id && id !== nilUUID);

const TABLE_COLUMNS = [
  { key: 'select', label: '', minWidth: 56, maxWidth: 90, defaultWidth: 56, resizable: false },
  { key: 'listing', label: 'Listing', minWidth: 220, maxWidth: 520, defaultWidth: 320 },
  { key: 'state', label: 'State', minWidth: 110, maxWidth: 220, defaultWidth: 130 },
  { key: 'quantity', label: 'Quantity', minWidth: 100, maxWidth: 200, defaultWidth: 120 },
  { key: 'section', label: 'Section', minWidth: 140, maxWidth: 320, defaultWidth: 180 },
  { key: 'price', label: 'Price', minWidth: 120, maxWidth: 240, defaultWidth: 140 },
  { key: 'updated', label: 'Updated', minWidth: 170, maxWidth: 340, defaultWidth: 210 },
];

const listingStateOptions = [
  { value: '', label: 'All states' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'expired', label: 'Expired' },
  { value: 'draft', label: 'Draft' },
  { value: 'sold_out', label: 'Sold out' },
];

const NO_SECTION_FILTER_VALUE = 'no_section';

const ListingsPage = () => {
  const { fetchWithAuth } = useAuth();
  const { latestJobUpdate, latestEntityUpdate } = useRealtime();
  const [shops, setShops] = React.useState([]);
  const [shopsLoading, setShopsLoading] = React.useState(false);
  const [selectedShopId, setSelectedShopId] = React.useState('');
  const [sections, setSections] = React.useState([]);
  const [sectionsLoading, setSectionsLoading] = React.useState(false);
  const [selectedSectionId, setSelectedSectionId] = React.useState('');
  const [selectedState, setSelectedState] = React.useState('');
  const [items, setItems] = React.useState([]);
  const [total, setTotal] = React.useState(0);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(defaultRowsPerPage);
  const [search, setSearch] = React.useState('');
  const [debouncedSearch, setDebouncedSearch] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [syncing, setSyncing] = React.useState(false);
	const [selectedIds, setSelectedIds] = React.useState(() => new Set());
  const [changeDialogOpen, setChangeDialogOpen] = React.useState(false);
  const [changeSectionId, setChangeSectionId] = React.useState('');
  const [changeError, setChangeError] = React.useState(null);
  const [changeSubmitting, setChangeSubmitting] = React.useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = React.useState(false);
  const [detailListing, setDetailListing] = React.useState(null);
  const detailListingId = detailListing?.id || '';
  const [detailLoading, setDetailLoading] = React.useState(false);
  const [productDialogOpen, setProductDialogOpen] = React.useState(false);
  const [selectedProduct, setSelectedProduct] = React.useState(null);
  const [productLoading, setProductLoading] = React.useState(false);
  const [printifyShops, setPrintifyShops] = React.useState([]);
  const [printifyShopsLoading, setPrintifyShopsLoading] = React.useState(false);
  const [actionsAnchorEl, setActionsAnchorEl] = React.useState(null);
  const [actionsProcessing, setActionsProcessing] = React.useState(false);
  const [actionMessage, setActionMessage] = React.useState(null);
  const [viewMode, setViewMode] = React.useState('tiles');
  const refreshTimerRef = React.useRef(null);
  const { getWidth, startColumnResize, tableMinWidth } = useResizableColumns(TABLE_COLUMNS, {
    storageKey: 'table.etsy-listings',
  });

  React.useEffect(() => {
    const handle = window.setTimeout(() => setDebouncedSearch(search.trim()), debounceDelay);
    return () => window.clearTimeout(handle);
  }, [search]);

  const loadShops = React.useCallback(async () => {
    setShopsLoading(true);
    try {
      const resp = await fetchWithAuth(`${config.apiBaseUrl}/etsy/accounts`);
      if (!resp.ok) {
        throw new Error('Failed to load Etsy shops');
      }
      const payload = await resp.json();
      const mapped = Array.isArray(payload.items)
        ? payload.items
            .map((acct) => {
              if (!acct.shop?.id) {
                return null;
              }
              return {
                id: acct.shop.id,
                title: acct.shop.title || acct.shop.name || acct.name,
                externalId: acct.shop.external_id,
              };
            })
            .filter(Boolean)
        : [];
      setShops(mapped);
      setSelectedShopId((current) => {
        const next = current && mapped.some((shop) => shop.id === current) ? current : mapped[0]?.id || '';
        if (next !== current) {
          setSelectedSectionId('');
        }
        return next;
      });
    } catch (err) {
      console.error(err);
    } finally {
      setShopsLoading(false);
    }
  }, [fetchWithAuth]);

  React.useEffect(() => {
    loadShops();
  }, [loadShops]);

  const loadPrintifyShops = React.useCallback(async () => {
    setPrintifyShopsLoading(true);
    try {
      const resp = await fetchWithAuth(`${config.apiBaseUrl}/printify/shops`);
      if (!resp.ok) {
        throw new Error('Failed to load Printify shops');
      }
      const payload = await resp.json();
      setPrintifyShops(Array.isArray(payload.items) ? payload.items : []);
    } catch (err) {
      console.error(err);
      setPrintifyShops([]);
    } finally {
      setPrintifyShopsLoading(false);
    }
  }, [fetchWithAuth]);

  React.useEffect(() => {
    loadPrintifyShops();
  }, [loadPrintifyShops]);

  const loadListings = React.useCallback(async () => {
    if (!selectedShopId) {
      setItems([]);
      setTotal(0);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set('shop_id', selectedShopId);
      params.set('limit', rowsPerPage.toString());
      params.set('offset', (page * rowsPerPage).toString());
      if (debouncedSearch) {
        params.set('q', debouncedSearch);
      }
      if (selectedSectionId) {
        params.set('section_id', selectedSectionId);
      }
      if (selectedState) {
        params.set('state', selectedState);
      }
      const resp = await fetchWithAuth(`${config.apiBaseUrl}/etsy/listings?${params.toString()}`);
      if (!resp.ok) {
        throw new Error('Failed to load listings');
      }
      const payload = await resp.json();
      setItems(Array.isArray(payload.items) ? payload.items : []);
      setTotal(typeof payload.total === 'number' ? payload.total : 0);
    } catch (err) {
      setError(err.message || 'Failed to load listings');
    } finally {
      setLoading(false);
    }
  }, [fetchWithAuth, selectedShopId, rowsPerPage, page, debouncedSearch, selectedSectionId, selectedState]);

  React.useEffect(() => {
    loadListings();
  }, [loadListings]);

  React.useEffect(() => {
    if (!latestJobUpdate || latestJobUpdate.type !== 'job.updated') {
      return;
    }
    const job = latestJobUpdate.job;
    if (!job || Number(job.status) !== 2) {
      return;
    }
    const trackedJobs = new Set([
      'SyncEtsyListings',
      'RenewExpiredEtsyListings',
      'ChangeEtsyListingState',
    ]);
    if (!trackedJobs.has(job.name)) {
      return;
    }
    if (refreshTimerRef.current) {
      window.clearTimeout(refreshTimerRef.current);
    }
    refreshTimerRef.current = window.setTimeout(() => {
      loadListings();
      if (job.name === 'SyncEtsyListings') {
        const params = new URLSearchParams();
        params.set('shop_id', selectedShopId);
        fetchWithAuth(`${config.apiBaseUrl}/etsy/sections?${params.toString()}`)
          .then((resp) => (resp.ok ? resp.json() : null))
          .then((payload) => {
            if (payload && Array.isArray(payload.items)) {
              setSections(payload.items);
            }
          })
          .catch(() => {});
      }
    }, 500);
    return () => {
      if (refreshTimerRef.current) {
        window.clearTimeout(refreshTimerRef.current);
      }
    };
  }, [latestJobUpdate, loadListings, selectedShopId, fetchWithAuth]);

  const listingMatchesCurrentFilters = React.useCallback(
    (listing) => {
      if (!listing) {
        return false;
      }
      if (selectedShopId && listing.shop_id && listing.shop_id !== selectedShopId) {
        return false;
      }
      if (selectedShopId && !listing.shop_id && listing.etsy_shop_id && listing.etsy_shop_id !== selectedShopId) {
        return false;
      }
      if (selectedSectionId === NO_SECTION_FILTER_VALUE) {
        if (listing.section?.id) {
          return false;
        }
      } else if (selectedSectionId && listing.section?.id !== selectedSectionId) {
        return false;
      }
      if (selectedState) {
        const listingState = String(listing.state || '').toLowerCase();
        if (selectedState === 'inactive') {
          if (listingState !== 'inactive' && listingState !== 'edit') {
            return false;
          }
        } else if (listingState !== selectedState) {
          return false;
        }
      }
      if (debouncedSearch) {
        const searchValue = debouncedSearch.toLowerCase();
        const title = String(listing.title || '').toLowerCase();
        const externalID = String(listing.external_id || '');
        if (!title.includes(searchValue) && !externalID.includes(debouncedSearch)) {
          return false;
        }
      }
      return true;
    },
    [debouncedSearch, selectedSectionId, selectedShopId, selectedState],
  );

  React.useEffect(() => {
    if (!latestEntityUpdate) {
      return;
    }
    if (latestEntityUpdate.type !== 'entity.updated' && latestEntityUpdate.type !== 'entity.deleted') {
      return;
    }
    if (latestEntityUpdate.entity_type !== 'etsy_listing') {
      return;
    }
    const entityID = latestEntityUpdate.entity_id;
    if (!entityID) {
      return;
    }

    if (latestEntityUpdate.type === 'entity.deleted') {
      setItems((prev) => removeEntityById(prev, entityID));
      setSelectedIds((prev) => {
        if (!prev.has(entityID)) {
          return prev;
        }
        const next = new Set(prev);
        next.delete(entityID);
        return next;
      });
      if (detailListingId === entityID) {
        setDetailDialogOpen(false);
        setDetailListing(null);
      }
      return;
    }

    let cancelled = false;
    const refreshEntity = async () => {
      try {
        const resp = await fetchWithAuth(`${config.apiBaseUrl}/etsy/listings/${entityID}?view=summary`);
        if (!resp.ok) {
          if (resp.status === 404) {
            setItems((prev) => removeEntityById(prev, entityID));
          }
          return;
        }
        const payload = await resp.json();
        const nextItem = payload?.item;
        if (!nextItem || cancelled) {
          return;
        }
        setItems((prev) => {
          const exists = prev.some((item) => item.id === entityID);
          if (!exists) {
            return prev;
          }
          if (!listingMatchesCurrentFilters(nextItem)) {
            return removeEntityById(prev, entityID);
          }
          return upsertEntityById(prev, nextItem);
        });
        if (detailListingId === entityID) {
          const detailResp = await fetchWithAuth(`${config.apiBaseUrl}/etsy/listings/${entityID}`);
          if (detailResp.ok) {
            const detailPayload = await detailResp.json();
            if (detailPayload?.item && !cancelled) {
              setDetailListing(detailPayload.item);
            }
          }
        }
      } catch (err) {
        console.warn('failed to refresh etsy listing entity', err);
      }
    };
    refreshEntity();
    return () => {
      cancelled = true;
    };
  }, [detailListingId, fetchWithAuth, latestEntityUpdate, listingMatchesCurrentFilters]);

  React.useEffect(() => {
    setPage(0);
  }, [selectedShopId, rowsPerPage, selectedSectionId, selectedState]);

  React.useEffect(() => {
    setSelectedIds(new Set());
  }, [items, selectedShopId]);

  React.useEffect(() => {
    const loadSections = async () => {
      if (!selectedShopId) {
        setSections([]);
        setSelectedSectionId('');
        return;
      }
      setSectionsLoading(true);
      try {
        const params = new URLSearchParams();
        params.set('shop_id', selectedShopId);
        const resp = await fetchWithAuth(`${config.apiBaseUrl}/etsy/sections?${params.toString()}`);
        if (!resp.ok) {
          throw new Error('Failed to load sections');
        }
        const payload = await resp.json();
        setSections(Array.isArray(payload.items) ? payload.items : []);
        setSelectedSectionId((current) => {
          if (!current) {
            return '';
          }
          if (current === NO_SECTION_FILTER_VALUE) {
            return current;
          }
          return payload.items?.some((section) => section.id === current) ? current : '';
        });
      } catch (err) {
        console.error(err);
        setSections([]);
        setSelectedSectionId('');
      } finally {
        setSectionsLoading(false);
      }
    };
    loadSections();
  }, [fetchWithAuth, selectedShopId]);

  const handleToggleAll = (event) => {
    if (event.target.checked) {
      const next = new Set(visibleListingIds);
      setSelectedIds(next);
    } else {
      setSelectedIds(new Set());
    }
  };

	const handleToggleRow = (id) => {
    if (!id) {
      return;
    }
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
	};

	const actionsOpen = Boolean(actionsAnchorEl);
	const openActionsMenu = (event) => {
		setActionsAnchorEl(event.currentTarget);
	};
	const closeActionsMenu = () => {
		setActionsAnchorEl(null);
	};

  const visibleListingIds = React.useMemo(
    () => items.map((item) => item.id).filter(Boolean),
    [items],
  );
	const selectedCount = selectedIds.size;
  const visibleSelectedCount = React.useMemo(
    () => visibleListingIds.filter((id) => selectedIds.has(id)).length,
    [visibleListingIds, selectedIds],
  );
  const selectedExpiredListingIds = React.useMemo(
    () => items
      .filter((item) => selectedIds.has(item.id) && String(item.state || '').toLowerCase() === 'expired')
      .map((item) => item.id)
      .filter(Boolean),
    [items, selectedIds],
  );
	const allSelected = visibleListingIds.length > 0 && visibleSelectedCount === visibleListingIds.length;
	const indeterminate = visibleSelectedCount > 0 && visibleSelectedCount < visibleListingIds.length;
  const actionsDisabled = !selectedShopId || selectedCount === 0 || actionsProcessing;

	const openChangeDialog = () => {
		const defaultSectionId = selectedSectionId === NO_SECTION_FILTER_VALUE ? '' : selectedSectionId;
		setChangeSectionId((current) => current || defaultSectionId || sections[0]?.id || '');
		setChangeError(null);
		setChangeDialogOpen(true);
	};

  const closeChangeDialog = () => {
    setChangeDialogOpen(false);
    setChangeError(null);
    setChangeSectionId('');
  };

  const handleListingUpdated = React.useCallback((updated) => {
    if (!updated) {
      return;
    }
    setItems((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
    setDetailListing(updated);
  }, []);

	const handleChangeSectionSubmit = async () => {
    if (!changeSectionId) {
      setChangeError('Select a section.');
      return;
    }
    setChangeSubmitting(true);
    setChangeError(null);
    try {
      const resp = await fetchWithAuth(`${config.apiBaseUrl}/etsy/listings/change-section`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          shop_id: selectedShopId,
          section_id: changeSectionId,
          listing_ids: Array.from(selectedIds),
        }),
      });
      if (!resp.ok) {
        const payload = await resp.json().catch(() => ({}));
        throw new Error(payload.error || 'Failed to change section');
      }
      closeChangeDialog();
      setSelectedIds(new Set());
      setActionMessage({ type: 'info', text: 'Section change queued. Progress is available in Team Jobs.' });
    } catch (err) {
      setChangeError(err.message || 'Failed to update listings');
    } finally {
      setChangeSubmitting(false);
    }
	};

  const handleListingStateChange = async (nextState) => {
    if (!selectedShopId) {
      setError('Select a shop before performing actions.');
      closeActionsMenu();
      return;
    }
		if (selectedIds.size === 0) {
			setError('Select at least one listing.');
			closeActionsMenu();
			return;
		}
    setActionsProcessing(true);
    setError(null);
    setActionMessage(null);
    closeActionsMenu();
    try {
			const resp = await fetchWithAuth(`${config.apiBaseUrl}/etsy/listings/change-state`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					shop_id: selectedShopId,
					listing_ids: Array.from(selectedIds),
					state: nextState,
				}),
			});
			const payload = await resp.json().catch(() => ({}));
			if (!resp.ok) {
				throw new Error(payload.error || 'Failed to update listings');
			}
			setSelectedIds(new Set());
      setActionMessage({ type: 'info', text: `Listing ${nextState} job queued. Progress is available in Team Jobs.` });
		} catch (err) {
			setError(err.message || 'Failed to update listings');
    } finally {
      setActionsProcessing(false);
    }
  };

  const handleRenewExpiredListings = async () => {
    if (!selectedShopId) {
      setError('Select a shop before performing actions.');
      closeActionsMenu();
      return;
    }
    if (selectedExpiredListingIds.length === 0) {
      setError('Select at least one expired listing.');
      closeActionsMenu();
      return;
    }
    const ignored = selectedCount - selectedExpiredListingIds.length;
    const confirmed = window.confirm(
      `Renew ${selectedExpiredListingIds.length} expired listing${selectedExpiredListingIds.length === 1 ? '' : 's'}? Etsy may charge listing renewal fees.`
        + (ignored > 0 ? ` ${ignored} selected non-expired listing${ignored === 1 ? '' : 's'} will be ignored.` : ''),
    );
    if (!confirmed) {
      closeActionsMenu();
      return;
    }
    setActionsProcessing(true);
    setError(null);
    setActionMessage(null);
    closeActionsMenu();
    try {
      const resp = await fetchWithAuth(`${config.apiBaseUrl}/etsy/listings/renew-expired`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          shop_id: selectedShopId,
          listing_ids: selectedExpiredListingIds,
        }),
      });
      const payload = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(payload.error || 'Failed to renew expired listings');
      }
      setSelectedIds(new Set());
      const queued = typeof payload.queued === 'number' ? payload.queued : selectedExpiredListingIds.length;
      setActionMessage({
        type: 'info',
        text: `Renewal queued for ${queued} expired listing${queued === 1 ? '' : 's'}. Progress is available in Team Jobs.`,
      });
    } catch (err) {
      setError(err.message || 'Failed to renew expired listings');
    } finally {
      setActionsProcessing(false);
    }
  };

  const handleRenewAllExpiredListings = async () => {
    if (!selectedShopId) {
      setError('Select a shop before performing actions.');
      return;
    }
    if (selectedState !== 'expired' || total === 0) {
      setError('Filter to expired listings before renewing all expired listings.');
      return;
    }
    const scope = selectedSectionId || debouncedSearch ? 'matching the current filters' : 'for this shop';
    const confirmed = window.confirm(
      `Renew all ${total} expired listing${total === 1 ? '' : 's'} ${scope}? Etsy may charge listing renewal fees.`,
    );
    if (!confirmed) {
      return;
    }
    setActionsProcessing(true);
    setError(null);
    setActionMessage(null);
    try {
      const resp = await fetchWithAuth(`${config.apiBaseUrl}/etsy/listings/renew-expired`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          shop_id: selectedShopId,
          all_expired: true,
          section_id: selectedSectionId || undefined,
          q: debouncedSearch || undefined,
        }),
      });
      const payload = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(payload.error || 'Failed to renew expired listings');
      }
      setSelectedIds(new Set());
      const queued = typeof payload.queued === 'number' ? payload.queued : total;
      setActionMessage({
        type: 'info',
        text: `Renewal queued for ${queued} expired listing${queued === 1 ? '' : 's'}. Progress is available in Team Jobs.`,
      });
    } catch (err) {
      setError(err.message || 'Failed to renew expired listings');
    } finally {
      setActionsProcessing(false);
    }
  };

  const handleDeleteListings = async () => {
    if (!selectedShopId) {
      setError('Select a shop before performing actions.');
      closeActionsMenu();
      return;
    }
    if (selectedIds.size === 0) {
      setError('Select at least one listing.');
      closeActionsMenu();
      return;
    }
    const confirmed = window.confirm(
      `Delete ${selectedIds.size} listing${selectedIds.size === 1 ? '' : 's'}? This cannot be undone.`,
    );
    if (!confirmed) {
      closeActionsMenu();
      return;
    }
    setActionsProcessing(true);
    setError(null);
    setActionMessage(null);
    closeActionsMenu();
    try {
      const resp = await fetchWithAuth(`${config.apiBaseUrl}/etsy/listings/delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          shop_id: selectedShopId,
          listing_ids: Array.from(selectedIds),
        }),
      });
      const payload = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(payload.error || 'Delete failed');
      }
      setSelectedIds(new Set());
      setActionMessage({ type: 'info', text: 'Delete job queued. Progress is available in Team Jobs.' });
    } catch (err) {
      setError(err.message || 'Delete failed');
    } finally {
      setActionsProcessing(false);
    }
  };

  const handleGenerateMockup = async () => {
    if (!selectedShopId) {
      setError('Select a shop before performing actions.');
      closeActionsMenu();
      return;
    }
    if (selectedIds.size === 0) {
      setError('Select at least one listing.');
      closeActionsMenu();
      return;
    }
    setActionsProcessing(true);
    setError(null);
    setActionMessage(null);
    closeActionsMenu();
    try {
      const resp = await fetchWithAuth(`${config.apiBaseUrl}/etsy/listings/generate-mockup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          shop_id: selectedShopId,
          listing_ids: Array.from(selectedIds),
        }),
      });
      const payload = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(payload.error || 'Failed to generate mockups');
      }
      setSelectedIds(new Set());
      const queued = typeof payload.queued === 'number' ? payload.queued : selectedIds.size;
      setActionMessage({
        type: 'info',
        text: `Generate mockup queued for ${queued} listing${queued === 1 ? '' : 's'}. Progress is available in Team Jobs.`,
      });
    } catch (err) {
      setError(err.message || 'Failed to generate mockups');
    } finally {
      setActionsProcessing(false);
    }
  };

  const handleSync = async () => {
    if (!selectedShopId) {
      return;
    }
    setSyncing(true);
    setError(null);
    setActionMessage(null);
    try {
      const resp = await fetchWithAuth(`${config.apiBaseUrl}/etsy/listings/sync`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ shop_id: selectedShopId }),
      });
      if (!resp.ok) {
        const payload = await resp.json().catch(() => ({}));
        throw new Error(payload.error || 'Sync failed');
      }
      setActionMessage({ type: 'info', text: 'Listings sync queued. Progress is available in Team Jobs.' });
    } catch (err) {
      setError(err.message || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatPrice = (amount, currency) => {
    if (!currency) {
      return '—';
    }
    const value = (amount || 0) / 100;
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
      }).format(value);
    } catch {
      return `${currency} ${value.toFixed(2)}`;
    }
  };

  const formatUpdatedAt = (value) => {
    if (!value) {
      return '—';
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return '—';
    }
    return parsed.toLocaleString();
  };

  const emptyStateLabel = selectedShopId
    ? 'No listings found for this shop.'
    : 'Select a shop to view listings.';

  const handleRowClick = async (listing) => {
    if (!listing) {
      return;
    }
    setDetailLoading(true);
    setDetailListing(listing);
    setDetailDialogOpen(true);
    try {
      const resp = await fetchWithAuth(`${config.apiBaseUrl}/etsy/listings/${listing.id}`);
      if (!resp.ok) {
        throw new Error('Failed to load listing details');
      }
      const payload = await resp.json();
      if (payload?.item) {
        let detailItem = payload.item;
        const hasImages = Array.isArray(detailItem.images) && detailItem.images.length > 0;
        if (!hasImages) {
          const imageResp = await fetchWithAuth(`${config.apiBaseUrl}/etsy/listings/${listing.id}/images?refresh=true`);
          if (imageResp.ok) {
            const imagePayload = await imageResp.json();
            if (imagePayload?.listing) {
              detailItem = imagePayload.listing;
            }
          }
        }
        handleListingUpdated(detailItem);
      }
    } catch (err) {
      setError(err.message || 'Failed to load listing details');
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetailDialog = () => {
    setDetailDialogOpen(false);
    setDetailListing(null);
    setDetailLoading(false);
  };

  const handleOpenLinkedProduct = React.useCallback(
    async (listing) => {
      const productID = listing?.printify_product_id;
      if (!hasLinkedPrintifyProduct(productID)) {
        return;
      }
      setDetailDialogOpen(false);
      setDetailListing(null);
      setDetailLoading(false);
      setProductDialogOpen(true);
      setProductLoading(true);
      setSelectedProduct(null);
      setError(null);
      try {
        if (printifyShops.length === 0 && !printifyShopsLoading) {
          loadPrintifyShops();
        }
        const resp = await fetchWithAuth(`${config.apiBaseUrl}/printify/products/${productID}`, {
          timeoutMs: config.longRequestTimeoutMs,
        });
        const payload = await resp.json().catch(() => ({}));
        if (!resp.ok || !payload?.item) {
          throw new Error(payload?.error || 'Failed to load linked Printify product');
        }
        setSelectedProduct(payload.item);
      } catch (err) {
        setProductDialogOpen(false);
        setError(err.message || 'Failed to load linked Printify product');
      } finally {
        setProductLoading(false);
      }
    },
    [fetchWithAuth, loadPrintifyShops, printifyShops.length, printifyShopsLoading],
  );

  const closeProductDialog = React.useCallback(() => {
    setProductDialogOpen(false);
    setSelectedProduct(null);
    setProductLoading(false);
  }, []);

  return (
    <Stack spacing={2} sx={{ height: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <TextField
          select
          label="Shop"
          value={selectedShopId}
          disabled={shopsLoading || shops.length === 0}
          onChange={(event) => setSelectedShopId(event.target.value)}
          sx={{ minWidth: 240 }}
        >
          {shops.map((shop) => (
          <MenuItem key={shop.id} value={shop.id}>
            {shop.title || shop.externalId}
          </MenuItem>
        ))}
        </TextField>
        <TextField
          select
          label="Section"
          value={selectedSectionId}
          onChange={(event) => setSelectedSectionId(event.target.value)}
          disabled={!selectedShopId || sectionsLoading}
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="">All sections</MenuItem>
          <MenuItem value={NO_SECTION_FILTER_VALUE}>No Section</MenuItem>
          {sections.map((section) => (
            <MenuItem key={section.id} value={section.id}>
              {section.title || section.external_id}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="State"
          value={selectedState}
          onChange={(event) => setSelectedState(event.target.value)}
          disabled={!selectedShopId}
          sx={{ minWidth: 160 }}
        >
          {listingStateOptions.map((option) => (
            <MenuItem key={option.value || 'all'} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Search listings"
          placeholder="Title or ID"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          sx={{ flexGrow: 1, minWidth: 240 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
		<Button
		  variant="outlined"
		  onClick={openActionsMenu}
		  endIcon={<KeyboardArrowDownIcon />}
		  disabled={actionsDisabled}
		>
		  Actions
		</Button>
        {selectedState === 'expired' ? (
          <Button
            variant="outlined"
            onClick={handleRenewAllExpiredListings}
            disabled={!selectedShopId || total === 0 || actionsProcessing}
          >
            Renew All Expired
          </Button>
        ) : null}
		<Menu
		  anchorEl={actionsAnchorEl}
		  open={actionsOpen}
		  onClose={closeActionsMenu}
		>
		  <MenuItem
		    onClick={() => {
		      closeActionsMenu();
		      openChangeDialog();
		    }}
		    disabled={sections.length === 0 || sectionsLoading}
		  >
		    Change Section
		  </MenuItem>
		  <MenuItem onClick={handleGenerateMockup} disabled={actionsProcessing}>
		    Generate Mockup
		  </MenuItem>
		  <MenuItem onClick={() => handleListingStateChange('active')} disabled={actionsProcessing}>
		    Activate
		  </MenuItem>
		  <MenuItem onClick={handleRenewExpiredListings} disabled={actionsProcessing || selectedExpiredListingIds.length === 0}>
		    Renew Expired
		  </MenuItem>
		  <MenuItem onClick={() => handleListingStateChange('inactive')} disabled={actionsProcessing}>
		    Deactivate
		  </MenuItem>
		  <MenuItem onClick={handleDeleteListings} disabled={actionsProcessing}>
		    Delete
		  </MenuItem>
		</Menu>
        <Button
          variant="contained"
          startIcon={<SyncIcon />}
          onClick={handleSync}
          disabled={!selectedShopId || syncing}
        >
          {syncing ? 'Syncing…' : 'Sync Listings'}
        </Button>
      </Box>

      {error ? <Alert severity="error">{error}</Alert> : null}
      {actionMessage ? <Alert severity={actionMessage.type}>{actionMessage.text}</Alert> : null}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Checkbox
            size="small"
            indeterminate={indeterminate}
            checked={allSelected}
            onChange={handleToggleAll}
            disabled={visibleListingIds.length === 0}
          />
          <Typography variant="body2" color="text.secondary">
            Select all
          </Typography>
        </Stack>
        <ViewModeToggle value={viewMode} onChange={setViewMode} />
      </Box>

      <Paper sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        {viewMode === 'rows' ? (
          <TableContainer sx={{ flex: 1 }}>
            <Table size="small" sx={{ tableLayout: 'fixed', minWidth: tableMinWidth }} stickyHeader>
              <ResizableColGroup columns={TABLE_COLUMNS} getWidth={getWidth} />
              <TableHead>
                <TableRow>
                  {TABLE_COLUMNS.map((column) => {
                    if (column.key === 'select') {
                      return (
                        <TableCell
                          key={column.key}
                          padding="checkbox"
                          sx={{
                            width: getWidth(column.key),
                            minWidth: getWidth(column.key),
                            maxWidth: getWidth(column.key),
                          }}
                        >
                          <Checkbox
                            indeterminate={indeterminate}
                            checked={allSelected}
                            onChange={handleToggleAll}
                            disabled={items.length === 0}
                          />
                        </TableCell>
                      );
                    }
                    return (
                      <ResizableHeaderCell
                        key={column.key}
                        column={column}
                        getWidth={getWidth}
                        onResizeStart={startColumnResize}
                      />
                    );
                  })}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableLoadingRow colSpan={TABLE_COLUMNS.length} label="Loading listings..." />
                ) : items.length === 0 ? (
                  <TableEmptyRow colSpan={TABLE_COLUMNS.length} label={emptyStateLabel} />
                ) : (
                  items.map((item) => (
                    <TableRow
                      key={item.id || item.external_id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => handleRowClick(item)}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedIds.has(item.id)}
                          onClick={(event) => event.stopPropagation()}
                          onChange={() => handleToggleRow(item.id)}
                        />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={2} alignItems="center">
                          {getListingPreviewUrl(item) ? (
                            <Avatar
                              variant="rounded"
                              src={getListingPreviewUrl(item)}
                              alt={item.title}
                              sx={{ width: 56, height: 56 }}
                            />
                          ) : (
                            <Avatar variant="rounded" sx={{ width: 56, height: 56 }}>
                              <StoreIcon />
                            </Avatar>
                          )}
                          <Box>
                            <Typography variant="subtitle2">
                              {item.title || item.external_id}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              ID: {item.external_id}
                            </Typography>
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={item.state || '—'}
                          size="small"
                          color={item.state === 'active' ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell>{item.quantity ?? '—'}</TableCell>
                      <TableCell>{item.section?.title || '—'}</TableCell>
                      <TableCell>{formatPrice(item.price?.amount, item.price?.currency)}</TableCell>
                      <TableCell>{formatUpdatedAt(item.updated_at)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', p: 2 }}>
            {loading ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={20} />
                <Typography variant="body2" color="text.secondary">
                  Loading listings...
                </Typography>
              </Stack>
            ) : items.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {emptyStateLabel}
              </Typography>
            ) : (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                  gap: 2,
                }}
              >
                {items.map((item) => {
                  const selected = selectedIds.has(item.id);
                  return (
                    <Paper
                      key={item.id || item.external_id}
                      variant="outlined"
                      onClick={() => handleRowClick(item)}
                      sx={{
                        cursor: 'pointer',
                        overflow: 'hidden',
                        borderColor: selected ? 'primary.main' : 'divider',
                        borderWidth: selected ? 2 : 1,
                      }}
                    >
                      <Box sx={{ position: 'relative', bgcolor: 'grey.100' }}>
                        {getListingPreviewUrl(item) ? (
                          <Box
                            component="img"
                            src={getListingPreviewUrl(item)}
                            alt={item.title || item.external_id}
                            sx={{
                              width: '100%',
                              aspectRatio: '1 / 1',
                              objectFit: 'cover',
                              display: 'block',
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              width: '100%',
                              aspectRatio: '1 / 1',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'text.secondary',
                            }}
                          >
                            <StoreIcon />
                          </Box>
                        )}
                        <Checkbox
                          checked={selected}
                          onClick={(event) => event.stopPropagation()}
                          onChange={() => handleToggleRow(item.id)}
                          sx={{
                            position: 'absolute',
                            top: 6,
                            left: 6,
                            bgcolor: 'rgba(255,255,255,0.85)',
                            borderRadius: 1,
                          }}
                        />
                        <Chip
                          label={item.state || '—'}
                          size="small"
                          color={item.state === 'active' ? 'success' : 'default'}
                          sx={{ position: 'absolute', top: 10, right: 10 }}
                        />
                      </Box>
                      <Stack spacing={0.5} sx={{ p: 1.5 }}>
                        <Typography variant="subtitle2" noWrap title={item.title || item.external_id}>
                          {item.title || item.external_id}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          ID: {item.external_id}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {formatPrice(item.price?.amount, item.price?.currency)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Qty: {item.quantity ?? '—'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap title={item.section?.title || '—'}>
                          Section: {item.section?.title || '—'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Updated: {formatUpdatedAt(item.updated_at)}
                        </Typography>
                      </Stack>
                    </Paper>
                  );
                })}
              </Box>
            )}
          </Box>
        )}
        <TablePagination
          component="div"
          rowsPerPageOptions={[10, 25, 50, 100]}
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(event, newPage) => setPage(newPage)}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      </Paper>

      <Dialog open={changeDialogOpen} onClose={closeChangeDialog} fullWidth maxWidth="sm">
        <DialogTitle>Change Section</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Move {selectedCount} listing{selectedCount === 1 ? '' : 's'} to a different section.
          </Typography>
          <TextField
            select
            label="Section"
            value={changeSectionId}
            onChange={(event) => setChangeSectionId(event.target.value)}
            fullWidth
          >
            {sections.map((section) => (
              <MenuItem key={section.id} value={section.id}>
                {section.title || section.external_id}
              </MenuItem>
            ))}
          </TextField>
          {changeError ? <Alert severity="error">{changeError}</Alert> : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeChangeDialog} disabled={changeSubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleChangeSectionSubmit}
            disabled={changeSubmitting || !changeSectionId}
          >
            {changeSubmitting ? 'Updating…' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      <ListingDetailDialog
        open={detailDialogOpen}
        listing={detailListing}
        loading={detailLoading}
        onClose={closeDetailDialog}
        formatPrice={formatPrice}
        fetchWithAuth={fetchWithAuth}
        onListingUpdated={handleListingUpdated}
        onOpenLinkedProduct={handleOpenLinkedProduct}
      />
      <ProductDetailDialog
        open={productDialogOpen}
        product={selectedProduct}
        loadingProduct={productLoading}
        shops={printifyShops}
        loadingShops={printifyShopsLoading}
        fetchWithAuth={fetchWithAuth}
        onClose={closeProductDialog}
        onSyncComplete={() => {}}
        onProductChange={(updated) => {
          if (updated) {
            setSelectedProduct(updated);
          }
        }}
        productTypeOptions={[]}
        variantTypeOptions={[]}
      />
    </Stack>
  );
};

const ListingDetailDialog = ({
  open,
  listing,
  loading,
  onClose,
  formatPrice,
  fetchWithAuth,
  onListingUpdated,
  onOpenLinkedProduct,
}) => {
  const fileInputRef = React.useRef(null);
  const [uploadingImage, setUploadingImage] = React.useState(false);
  const [deletingImageId, setDeletingImageId] = React.useState(null);
  const [featuringImageId, setFeaturingImageId] = React.useState(null);
  const [deletingSelectedImages, setDeletingSelectedImages] = React.useState(false);
  const [imageError, setImageError] = React.useState(null);
  const [imageInfo, setImageInfo] = React.useState(null);
  const [selectedImageIds, setSelectedImageIds] = React.useState(() => new Set());
  const [draggingImageId, setDraggingImageId] = React.useState(null);
  const [reorderingImages, setReorderingImages] = React.useState(false);
  const [detailTab, setDetailTab] = React.useState('mockups');
  const [editTitle, setEditTitle] = React.useState('');
  const [editDescription, setEditDescription] = React.useState('');
  const [contentSaving, setContentSaving] = React.useState(false);
  const [contentError, setContentError] = React.useState(null);
  const [contentInfo, setContentInfo] = React.useState(null);
  const [linkDialogOpen, setLinkDialogOpen] = React.useState(false);
  const [linkProductId, setLinkProductId] = React.useState('');
  const [linkProductExternalId, setLinkProductExternalId] = React.useState('');
  const [linkSubmitting, setLinkSubmitting] = React.useState(false);
  const [linkError, setLinkError] = React.useState(null);

  const getImageFilename = React.useCallback((url) => {
    if (!url) {
      return '';
    }
    try {
      const parsed = new URL(url);
      const segments = parsed.pathname.split('/').filter(Boolean);
      return segments[segments.length - 1] || url;
    } catch {
      const parts = url.split('/').filter(Boolean);
      return parts[parts.length - 1] || url;
    }
  }, []);

  const images = React.useMemo(() => {
    if (!listing || !Array.isArray(listing.images)) {
      return [];
    }
    return listing.images;
  }, [listing]);

  const videos = React.useMemo(() => {
    if (!listing || !Array.isArray(listing.videos)) {
      return [];
    }
    return listing.videos;
  }, [listing]);

  const mediaItems = React.useMemo(
    () => [
      ...images.map((image) => ({ type: 'image', image })),
      ...videos.map((video) => ({ type: 'video', video })),
    ],
    [images, videos],
  );

  const tags = React.useMemo(() => {
    if (!listing) {
      return [];
    }
    if (Array.isArray(listing.tags)) {
      return listing.tags;
    }
    if (typeof listing.tags === 'string' && listing.tags.length > 0) {
      return listing.tags.split(',').map((tag) => tag.trim()).filter(Boolean);
    }
    return [];
  }, [listing]);

  const sectionLabel = listing?.section?.title || listing?.section?.external_id || '—';
  const formattedRawData = React.useMemo(() => {
    const raw = listing?.raw_data || listing?.RawData || '';
    if (!raw) {
      return 'No data available.';
    }
    if (typeof raw !== 'string') {
      try {
        return JSON.stringify(raw, null, 2);
      } catch {
        return String(raw);
      }
    }
    try {
      const parsed = JSON.parse(raw);
      return JSON.stringify(parsed, null, 2);
    } catch {
      return raw;
    }
  }, [listing]);

  React.useEffect(() => {
    setImageError(null);
    setImageInfo(null);
    setDeletingImageId(null);
    setFeaturingImageId(null);
    setDeletingSelectedImages(false);
    setUploadingImage(false);
    setSelectedImageIds(new Set());
    setDraggingImageId(null);
    setReorderingImages(false);
    setDetailTab('mockups');
    setEditTitle(listing?.title || '');
    setEditDescription(listing?.description || '');
    setContentSaving(false);
    setContentError(null);
    setContentInfo(null);
    setLinkDialogOpen(false);
    setLinkProductId('');
    setLinkSubmitting(false);
    setLinkError(null);
  }, [listing?.id]);

  React.useEffect(() => {
    if (!contentSaving) {
      setEditTitle(listing?.title || '');
      setEditDescription(listing?.description || '');
    }
  }, [contentSaving, listing?.description, listing?.title]);

  const contentDirty = React.useMemo(() => {
    const originalTitle = String(listing?.title || '').trim();
    const originalDescription = String(listing?.description || '').trim();
    return editTitle.trim() !== originalTitle || editDescription.trim() !== originalDescription;
  }, [editDescription, editTitle, listing?.description, listing?.title]);

  const resetContentEdits = React.useCallback(() => {
    setEditTitle(listing?.title || '');
    setEditDescription(listing?.description || '');
    setContentError(null);
    setContentInfo(null);
  }, [listing?.description, listing?.title]);

  const handleSaveContent = React.useCallback(async () => {
    if (!listing?.id || !contentDirty) {
      return;
    }
    const title = editTitle.trim();
    if (!title) {
      setContentError('Title cannot be empty.');
      return;
    }
    const payload = {};
    if (title !== String(listing?.title || '').trim()) {
      payload.title = title;
    }
    const description = editDescription.trim();
    if (description !== String(listing?.description || '').trim()) {
      payload.description = description;
    }
    setContentSaving(true);
    setContentError(null);
    setContentInfo(null);
    try {
      const resp = await fetchWithAuth(`${config.apiBaseUrl}/etsy/listings/${listing.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        timeoutMs: config.longRequestTimeoutMs,
      });
      const responsePayload = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(responsePayload.error || 'Failed to save listing content');
      }
      if (responsePayload.item) {
        setEditTitle(responsePayload.item.title || '');
        setEditDescription(responsePayload.item.description || '');
        onListingUpdated?.(responsePayload.item);
      }
      setContentInfo('Listing content saved.');
    } catch (err) {
      setContentError(err.message || 'Failed to save listing content');
    } finally {
      setContentSaving(false);
    }
  }, [contentDirty, editDescription, editTitle, fetchWithAuth, listing, onListingUpdated]);

  const handleUploadClick = React.useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  }, []);

  const handleFileChange = React.useCallback(
    async (event) => {
      const file = event.target.files?.[0];
      if (!file || !listing?.id) {
        return;
      }
      setUploadingImage(true);
      setImageError(null);
      try {
        const formData = new FormData();
        formData.append('image', file);
        if (images.length > 0) {
          formData.append('rank', (images.length + 1).toString());
        }
        const resp = await fetchWithAuth(`${config.apiBaseUrl}/etsy/listings/${listing.id}/images`, {
          method: 'POST',
          body: formData,
          timeoutMs: config.longRequestTimeoutMs,
        });
        const payload = await resp.json().catch(() => ({}));
        if (!resp.ok) {
          throw new Error(payload.error || 'Upload failed');
        }
        if (payload.listing) {
          onListingUpdated?.(payload.listing);
        }
        setImageInfo('Image uploaded successfully.');
      } catch (err) {
        setImageError(err.message || 'Upload failed');
      } finally {
        setUploadingImage(false);
        if (event.target) {
          event.target.value = '';
        }
      }
    },
    [fetchWithAuth, images.length, listing, onListingUpdated],
  );

  const handleDeleteImage = React.useCallback(
    async (imageId) => {
      if (!imageId || !listing?.id) {
        return;
      }
      setDeletingImageId(imageId);
      setImageError(null);
      try {
        const resp = await fetchWithAuth(
          `${config.apiBaseUrl}/etsy/listings/${listing.id}/images/${imageId}`,
          {
            method: 'DELETE',
            timeoutMs: config.longRequestTimeoutMs,
          },
        );
        const payload = await resp.json().catch(() => ({}));
        if (!resp.ok) {
          throw new Error(payload.error || 'Delete failed');
        }
        if (payload.listing) {
          onListingUpdated?.(payload.listing);
        }
        setSelectedImageIds((prev) => {
          if (!prev.has(imageId)) {
            return prev;
          }
          const next = new Set(prev);
          next.delete(imageId);
          return next;
        });
        setImageInfo('Image deleted successfully.');
      } catch (err) {
        setImageError(err.message || 'Delete failed');
      } finally {
        setDeletingImageId(null);
      }
    },
    [fetchWithAuth, listing, onListingUpdated],
  );

  const handleFeatureImage = React.useCallback(
    async (image) => {
      if (!image?.id || !listing?.id) {
        return;
      }
      if (image.rank === 1) {
        return;
      }
      setFeaturingImageId(image.id);
      setImageError(null);
      setImageInfo(null);
      try {
        const resp = await fetchWithAuth(
          `${config.apiBaseUrl}/etsy/listings/${listing.id}/images/${image.id}/feature`,
          {
            method: 'POST',
            timeoutMs: config.longRequestTimeoutMs,
          },
        );
        const payload = await resp.json().catch(() => ({}));
        if (!resp.ok) {
          throw new Error(payload.error || 'Failed to update featured mockup');
        }
        if (payload.listing) {
          onListingUpdated?.(payload.listing);
        }
        setImageInfo('Featured mockup updated.');
      } catch (err) {
        setImageError(err.message || 'Failed to update featured mockup');
      } finally {
        setFeaturingImageId(null);
      }
    },
    [fetchWithAuth, listing, onListingUpdated],
  );

  const imageIds = React.useMemo(
    () => images.map((image) => image.id).filter(Boolean),
    [images],
  );
  const selectedImageCount = selectedImageIds.size;
  const visibleSelectedImageCount = React.useMemo(
    () => imageIds.filter((id) => selectedImageIds.has(id)).length,
    [imageIds, selectedImageIds],
  );
  const allImagesSelected = imageIds.length > 0 && visibleSelectedImageCount === imageIds.length;
  const someImagesSelected = visibleSelectedImageCount > 0 && visibleSelectedImageCount < imageIds.length;
  const linkedPrintifyProductID = listing?.printify_product_id;

  const handleToggleAllImages = React.useCallback((event) => {
    if (event.target.checked) {
      setSelectedImageIds(new Set(imageIds));
    } else {
      setSelectedImageIds(new Set());
    }
  }, [imageIds]);

  const handleToggleImage = React.useCallback((imageId) => {
    if (!imageId) {
      return;
    }
    setSelectedImageIds((prev) => {
      const next = new Set(prev);
      if (next.has(imageId)) {
        next.delete(imageId);
      } else {
        next.add(imageId);
      }
      return next;
    });
  }, []);

  const handleDeleteSelectedImages = React.useCallback(async () => {
    if (!listing?.id || selectedImageIds.size === 0) {
      return;
    }
    setDeletingSelectedImages(true);
    setImageError(null);
    setImageInfo(null);
    try {
      const resp = await fetchWithAuth(`${config.apiBaseUrl}/etsy/listings/${listing.id}/images/delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ image_ids: Array.from(selectedImageIds) }),
        timeoutMs: config.longRequestTimeoutMs,
      });
      const payload = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(payload.error || 'Failed to queue image deletion');
      }
      setSelectedImageIds(new Set());
      setImageInfo('Delete queued. Progress is available in Team Jobs.');
    } catch (err) {
      setImageError(err.message || 'Failed to queue image deletion');
    } finally {
      setDeletingSelectedImages(false);
    }
  }, [fetchWithAuth, listing?.id, selectedImageIds]);

  const buildReorderedImageIds = React.useCallback((sourceId, targetId) => {
    if (!sourceId || !targetId || sourceId === targetId) {
      return null;
    }
    const ids = images.map((image) => image.id).filter(Boolean);
    const sourceIndex = ids.indexOf(sourceId);
    const targetIndex = ids.indexOf(targetId);
    if (sourceIndex < 0 || targetIndex < 0) {
      return null;
    }
    const next = [...ids];
    const [moved] = next.splice(sourceIndex, 1);
    next.splice(targetIndex, 0, moved);
    return next;
  }, [images]);

  const handleImageDrop = React.useCallback(
    async (sourceImageId, targetImageId) => {
      const nextImageIds = buildReorderedImageIds(sourceImageId, targetImageId);
      setDraggingImageId(null);
      if (!listing?.id || !nextImageIds) {
        return;
      }
      setReorderingImages(true);
      setImageError(null);
      setImageInfo(null);
      try {
        const resp = await fetchWithAuth(`${config.apiBaseUrl}/etsy/listings/${listing.id}/images/reorder`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ image_ids: nextImageIds }),
          timeoutMs: config.longRequestTimeoutMs,
        });
        const payload = await resp.json().catch(() => ({}));
        if (!resp.ok) {
          throw new Error(payload.error || 'Failed to reorder listing images');
        }
        if (payload.listing) {
          onListingUpdated?.(payload.listing);
        }
        setImageInfo('Image order updated.');
      } catch (err) {
        setImageError(err.message || 'Failed to reorder listing images');
      } finally {
        setReorderingImages(false);
      }
    },
    [buildReorderedImageIds, fetchWithAuth, listing?.id, onListingUpdated],
  );

  const handleOpenLinkDialog = React.useCallback(() => {
    setLinkProductId('');
    setLinkProductExternalId('');
    setLinkError(null);
    setLinkDialogOpen(true);
  }, []);

  const handleCloseLinkDialog = React.useCallback(() => {
    if (linkSubmitting) {
      return;
    }
    setLinkDialogOpen(false);
    setLinkError(null);
    setLinkProductId('');
    setLinkProductExternalId('');
  }, [linkSubmitting]);

  const handleLinkProduct = React.useCallback(async () => {
    if (!listing?.id) {
      return;
    }
    const productID = linkProductId.trim();
    const productExternalID = linkProductExternalId.trim();
    if (!productID && !productExternalID) {
      setLinkError('Enter a Printify product UUID or external ID.');
      return;
    }
    setLinkSubmitting(true);
    setLinkError(null);
    try {
      const body = productID
        ? { printify_product_id: productID }
        : { printify_product_external_id: productExternalID };
      const resp = await fetchWithAuth(`${config.apiBaseUrl}/etsy/listings/${listing.id}/link-product`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const payload = await resp.json().catch(() => ({}));
      if (!resp.ok || !payload?.item) {
        throw new Error(payload?.error || 'Failed to link Printify product');
      }
      onListingUpdated?.(payload.item);
      setLinkDialogOpen(false);
      setLinkProductId('');
      setLinkProductExternalId('');
    } catch (err) {
      setLinkError(err.message || 'Failed to link Printify product');
    } finally {
      setLinkSubmitting(false);
    }
  }, [fetchWithAuth, linkProductExternalId, linkProductId, listing?.id, onListingUpdated]);

  return (
    <>
      <DetailModalShell
        open={open}
        onClose={onClose}
        title={loading ? 'Loading listing...' : listing?.title || 'Listing Details'}
        externalId={listing?.external_id || listing?.id}
      left={
        loading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 240 }}>
            <CircularProgress size={24} />
          </Box>
        ) : listing ? (
          <MockupsDataTabs
            value={detailTab}
            onChange={setDetailTab}
            rawData={formattedRawData}
            mockupsToolbar={
              <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center" sx={{ mb: 1, flexWrap: 'wrap' }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="subtitle2">Media</Typography>
                  <Chip size="small" label={`${images.length} image${images.length === 1 ? '' : 's'}`} />
                  {videos.length > 0 ? (
                    <Chip size="small" variant="outlined" label={`${videos.length} video${videos.length === 1 ? '' : 's'}`} />
                  ) : null}
                  <Checkbox
                    size="small"
                    indeterminate={someImagesSelected}
                    checked={allImagesSelected}
                    onChange={handleToggleAllImages}
                    disabled={imageIds.length === 0 || reorderingImages}
                    inputProps={{ 'aria-label': 'select all listing images' }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    Select images
                  </Typography>
                  <Button
                    size="small"
                    color="error"
                    variant="outlined"
                    startIcon={<DeleteIcon />}
                    onClick={handleDeleteSelectedImages}
                    disabled={deletingSelectedImages || reorderingImages || selectedImageCount === 0 || !listing?.id}
                  >
                    {deletingSelectedImages ? 'Deleting…' : `Delete (${selectedImageCount})`}
                  </Button>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  {reorderingImages ? <CircularProgress size={18} /> : null}
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AddPhotoAlternateIcon />}
                    onClick={handleUploadClick}
                    disabled={uploadingImage || reorderingImages || !listing?.id}
                  >
                    {uploadingImage ? 'Uploading…' : 'Upload Image'}
                  </Button>
                  <input
                    type="file"
                    accept="image/*"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                  />
                </Stack>
              </Stack>
            }
            mockupsContent={
              <>
                {mediaItems.length > 0 ? (
                  <ImageList
                    cols={3}
                    gap={8}
                    sx={{
                      m: 0,
                      maxHeight: 'calc(100vh - 240px)',
                      overflowY: 'auto',
                      pr: 1,
                      '& img, & video': {
                        width: '100%',
                        aspectRatio: '1 / 1',
                        objectFit: 'cover',
                        borderRadius: 2,
                        display: 'block',
                        bgcolor: 'grey.100',
                      },
                    }}
                  >
                    {mediaItems.map((item, index) => {
                      if (item.type === 'video') {
                        const video = item.video;
                        const videoUrl = video.video_url || video.url || '';
                        const thumbnailUrl = video.thumbnail_url || video.thumbnail || '';
                        return (
                          <ImageListItem key={`video-${video.id || video.external_id || index}`} sx={{ position: 'relative' }}>
                            <Box
                              sx={{
                                position: 'absolute',
                                top: 8,
                                left: 8,
                                bgcolor: 'rgba(0,0,0,0.65)',
                                color: '#fff',
                                borderRadius: 1,
                                px: 1,
                                py: 0.25,
                                fontSize: 12,
                                fontWeight: 500,
                                zIndex: 1,
                              }}
                            >
                              Video
                            </Box>
                            {videoUrl ? (
                              <Box
                                component="video"
                                src={videoUrl}
                                poster={thumbnailUrl || undefined}
                                title={getImageFilename(videoUrl)}
                                controls
                                preload="metadata"
                              />
                            ) : thumbnailUrl ? (
                              <Box
                                component="img"
                                src={thumbnailUrl}
                                alt={`${listing?.title || 'Listing'} video thumbnail`}
                                title={getImageFilename(thumbnailUrl)}
                                loading="lazy"
                              />
                            ) : (
                              <Box
                                sx={{
                                  aspectRatio: '1 / 1',
                                  borderRadius: 2,
                                  bgcolor: 'action.hover',
                                  color: 'text.secondary',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  px: 1,
                                  textAlign: 'center',
                                }}
                              >
                                Video unavailable
                              </Box>
                            )}
                          </ImageListItem>
                        );
                      }

                      const image = item.image;
                      const imageDraggable = Boolean(image.id) && images.length > 1 && !reorderingImages;
                      const isDragging = draggingImageId === image.id;
                      return (
                        <ImageListItem
                          key={`image-${image.id || image.external_id || index}`}
                          draggable={imageDraggable}
                          onDragStart={(event) => {
                            if (!imageDraggable) {
                              return;
                            }
                            event.dataTransfer.effectAllowed = 'move';
                            event.dataTransfer.setData('text/plain', image.id);
                            setDraggingImageId(image.id);
                          }}
                          onDragOver={(event) => {
                            if (draggingImageId && draggingImageId !== image.id) {
                              event.preventDefault();
                              event.dataTransfer.dropEffect = 'move';
                            }
                          }}
                          onDrop={(event) => {
                            const sourceImageId = event.dataTransfer.getData('text/plain') || draggingImageId;
                            if (sourceImageId && sourceImageId !== image.id) {
                              event.preventDefault();
                              handleImageDrop(sourceImageId, image.id);
                            }
                          }}
                          onDragEnd={() => setDraggingImageId(null)}
                          sx={{
                            position: 'relative',
                            cursor: imageDraggable ? (isDragging ? 'grabbing' : 'grab') : 'default',
                            opacity: isDragging ? 0.55 : 1,
                            outline: draggingImageId && draggingImageId !== image.id ? '2px dashed' : 'none',
                            outlineColor: 'primary.main',
                            outlineOffset: 2,
                          }}
                        >
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 8,
                              left: 8,
                              bgcolor: 'rgba(0,0,0,0.65)',
                              color: '#fff',
                              borderRadius: 1,
                              px: 1,
                              py: 0.25,
                              fontSize: 12,
                              fontWeight: 500,
                              zIndex: 1,
                            }}
                          >
                            #{image.rank || index + 1}
                          </Box>
                          <Box
                            component="img"
                            src={image.url}
                            alt={listing?.title || 'Listing mockup'}
                            title={getImageFilename(image.url)}
                            loading="lazy"
                            draggable={false}
                          />
                          <IconButton
                            size="small"
                            sx={{
                              position: 'absolute',
                              top: 8,
                              right: 80,
                              bgcolor: 'rgba(0,0,0,0.55)',
                              color: '#fff',
                              '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' },
                            }}
                            onClick={(event) => {
                              event.stopPropagation();
                              handleFeatureImage(image);
                            }}
                            disabled={reorderingImages || featuringImageId === image.id || !listing?.id}
                          >
                            {image.rank === 1 ? (
                              <StarIcon fontSize="small" />
                            ) : featuringImageId === image.id ? (
                              <CircularProgress size={16} color="inherit" />
                            ) : (
                              <StarBorderIcon fontSize="small" />
                            )}
                          </IconButton>
                          <IconButton
                            size="small"
                            sx={{
                              position: 'absolute',
                              top: 8,
                              right: 44,
                              bgcolor: 'rgba(255,255,255,0.85)',
                              borderRadius: 1,
                            }}
                            onClick={(event) => {
                              event.stopPropagation();
                              handleToggleImage(image.id);
                            }}
                            disabled={reorderingImages || !listing?.id}
                          >
                            <Checkbox
                              size="small"
                              checked={selectedImageIds.has(image.id)}
                              disabled={reorderingImages || !listing?.id}
                              sx={{ p: 0.25 }}
                            />
                          </IconButton>
                          <IconButton
                            size="small"
                            sx={{
                              position: 'absolute',
                              top: 8,
                              right: 8,
                              bgcolor: 'rgba(0,0,0,0.55)',
                              color: '#fff',
                              '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' },
                            }}
                            onClick={() => handleDeleteImage(image.id)}
                            disabled={reorderingImages || deletingImageId === image.id || !listing?.id}
                          >
                            {deletingImageId === image.id ? (
                              <CircularProgress size={16} color="inherit" />
                            ) : (
                              <DeleteIcon fontSize="small" />
                            )}
                          </IconButton>
                        </ImageListItem>
                      );
                    })}
                  </ImageList>
                ) : getListingPreviewUrl(listing) ? (
                  <Box
                    component="img"
                    src={getListingPreviewUrl(listing)}
                    alt={listing?.title || 'Listing preview'}
                    sx={{ width: '100%', borderRadius: 2 }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: '100%',
                      minHeight: 220,
                      border: '1px dashed',
                      borderColor: 'divider',
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'text.secondary',
                    }}
                  >
                    No media available
                  </Box>
                )}
                {imageError ? (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    {imageError}
                  </Alert>
                ) : null}
                {imageInfo ? (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    {imageInfo}
                  </Alert>
                ) : null}
              </>
            }
          />
        ) : (
          <Typography color="text.secondary">No listing selected.</Typography>
        )
      }
      right={
        loading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 240 }}>
            <CircularProgress size={24} />
          </Box>
        ) : listing ? (
          <Stack spacing={1.5} sx={{ flex: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip
                label={listing?.state || '—'}
                color={listing?.state === 'active' ? 'success' : 'default'}
                size="small"
              />
              {listing?.url ? (
                <Button
                  size="small"
                  variant="outlined"
                  component="a"
                  href={listing.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View on Etsy
                </Button>
              ) : null}
              {hasLinkedPrintifyProduct(linkedPrintifyProductID) ? (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Inventory2OutlinedIcon />}
                  onClick={() => onOpenLinkedProduct?.(listing)}
                  disabled={loading}
                >
                  View Product
                </Button>
              ) : (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AddLinkIcon />}
                  onClick={handleOpenLinkDialog}
                  disabled={loading || !listing?.id}
                >
                  Link Product
                </Button>
              )}
            </Stack>
            <Typography variant="body2" color="text.secondary">
              Updated: {listing?.updated_at ? new Date(listing.updated_at).toLocaleString() : '—'}
            </Typography>
            <Stack spacing={1.25}>
              <Typography variant="subtitle2">Content</Typography>
              <TextField
                label="Title"
                value={editTitle}
                onChange={(event) => {
                  setEditTitle(event.target.value);
                  setContentError(null);
                  setContentInfo(null);
                }}
                fullWidth
                size="small"
                inputProps={{ maxLength: 140 }}
                helperText={`${editTitle.trim().length}/140`}
                disabled={contentSaving}
              />
              <TextField
                label="Description"
                value={editDescription}
                onChange={(event) => {
                  setEditDescription(event.target.value);
                  setContentError(null);
                  setContentInfo(null);
                }}
                fullWidth
                multiline
                minRows={8}
                disabled={contentSaving}
              />
              {contentError ? <Alert severity="error">{contentError}</Alert> : null}
              {contentInfo ? <Alert severity="success">{contentInfo}</Alert> : null}
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button onClick={resetContentEdits} disabled={contentSaving || !contentDirty}>
                  Reset
                </Button>
                <Button
                  variant="contained"
                  startIcon={contentSaving ? <CircularProgress color="inherit" size={16} /> : <SaveIcon />}
                  onClick={handleSaveContent}
                  disabled={contentSaving || !contentDirty || !editTitle.trim()}
                >
                  {contentSaving ? 'Saving…' : 'Save'}
                </Button>
              </Stack>
            </Stack>
            <Divider />
            <Stack spacing={1}>
              <InfoRow label="Section" value={sectionLabel} />
              <InfoRow label="Quantity" value={listing?.quantity ?? '—'} />
              <InfoRow
                label="Price"
                value={formatPrice(listing?.price?.amount, listing?.price?.currency)}
              />
              <InfoRow label="Readiness" value={listing?.readiness_state_id || '—'} />
              <InfoRow label="Language" value={listing?.language || '—'} />
            </Stack>
            <Divider />
            <Stack spacing={1}>
              <Typography variant="subtitle2">Tags</Typography>
              {tags.length > 0 ? (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {tags.map((tag) => (
                    <Chip key={tag} label={tag} size="small" />
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No tags set.
                </Typography>
              )}
            </Stack>
          </Stack>
        ) : (
          <Typography color="text.secondary">No listing selected.</Typography>
        )
      }
      />
      <Dialog open={linkDialogOpen} onClose={handleCloseLinkDialog} fullWidth maxWidth="sm">
        <DialogTitle>Link Printify Product</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              Enter either the local Printify product UUID or the Printify external ID. If both are filled, the UUID is used.
            </Typography>
            <TextField
              label="Printify product UUID"
              value={linkProductId}
              onChange={(event) => {
                setLinkProductId(event.target.value);
                setLinkError(null);
              }}
              fullWidth
              autoFocus
              disabled={linkSubmitting}
            />
            <TextField
              label="Printify product external ID"
              value={linkProductExternalId}
              onChange={(event) => {
                setLinkProductExternalId(event.target.value);
                setLinkError(null);
              }}
              fullWidth
              disabled={linkSubmitting}
              helperText="This is the Printify product ID shown in the Product modal."
            />
            {linkError ? <Alert severity="error">{linkError}</Alert> : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseLinkDialog} disabled={linkSubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleLinkProduct}
            disabled={linkSubmitting || (!linkProductId.trim() && !linkProductExternalId.trim())}
          >
            {linkSubmitting ? 'Linking…' : 'Link Product'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const InfoRow = ({ label, value }) => (
  <Stack direction="row" spacing={1} justifyContent="space-between">
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2">{value ?? '—'}</Typography>
  </Stack>
);

export default ListingsPage;
